/**
 * Progressive Transcription Status Hook
 *
 * Tracks the status of progressive transcription jobs for a session:
 * - Subscribes to transcription_jobs via Supabase Realtime
 * - Provides status per phase (briefing, simulator_main, simulator_tail, debriefing)
 * - Handles T-15 early trigger check and API call
 * - Reports overall readiness state
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

// ── Types ──────────────────────────────────────────────────────────────────

export type ProgressivePhase =
  | 'briefing'
  | 'simulator_main'
  | 'simulator_tail'
  | 'simulator_stitched'
  | 'debriefing';

export type PhaseStatus = 'idle' | 'pending' | 'processing' | 'completed' | 'failed' | 'stitching';

export interface PhaseJob {
  id: string;
  phase: string;
  status: PhaseStatus;
  assemblyaiTranscriptId: string | null;
  audioDurationMs: number | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface ProgressiveTranscriptionState {
  /** Status of each transcription phase */
  phases: Record<ProgressivePhase, PhaseStatus>;
  /** Detailed job records */
  jobs: PhaseJob[];
  /** Whether any phase is currently processing */
  isProcessing: boolean;
  /** Whether all expected phases are complete */
  isComplete: boolean;
  /** Whether T-15 trigger has fired */
  earlyTriggered: boolean;
  /** Trigger early transcription manually */
  triggerEarlyTranscription: () => Promise<void>;
  /** Trigger a specific phase */
  triggerPhase: (phase: ProgressivePhase) => Promise<void>;
}

// ── Hook ────────────────────────────────────────────────────────────────────

export function useProgressiveTranscription(
  sessionId: string | undefined,
): ProgressiveTranscriptionState {
  const [jobs, setJobs] = useState<PhaseJob[]>([]);
  const [earlyTriggered, setEarlyTriggered] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Load existing jobs ──────────────────────────────────────────────

  const loadJobs = useCallback(async () => {
    if (!sessionId) return;

    const { data, error } = await supabase
      .from('transcription_jobs')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at');

    if (error) {
      console.warn('[useProgressiveTranscription] Failed to load jobs:', error.message);
      return;
    }

    setJobs(
      (data || []).map((j) => ({
        id: j.id,
        phase: j.phase,
        status: j.status as PhaseStatus,
        assemblyaiTranscriptId: j.assemblyai_transcript_id,
        audioDurationMs: j.audio_duration_ms,
        errorMessage: j.error_message,
        createdAt: j.created_at,
        completedAt: j.processing_completed_at,
      }))
    );
  }, [sessionId]);

  // ── Initial load ────────────────────────────────────────────────────

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // ── Real-time subscription ──────────────────────────────────────────

  useEffect(() => {
    if (!sessionId) return;

    const channel = supabase
      .channel(`progressive-transcription-${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'transcription_jobs',
          filter: `session_id=eq.${sessionId}`,
        },
        () => {
          // Reload all jobs on any change
          loadJobs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId, loadJobs]);

  // ── Check early trigger status ──────────────────────────────────────

  useEffect(() => {
    if (!sessionId) return;

    const checkTrigger = async () => {
      const { data } = await supabase
        .from('sessions')
        .select('early_transcription_triggered')
        .eq('id', sessionId)
        .single();

      if (data?.early_transcription_triggered) {
        setEarlyTriggered(true);
      }
    };

    checkTrigger();
  }, [sessionId]);

  // ── Derive phase statuses ──────────────────────────────────────────

  const phaseStatusMap: Record<ProgressivePhase, PhaseStatus> = {
    briefing: 'idle',
    simulator_main: 'idle',
    simulator_tail: 'idle',
    simulator_stitched: 'idle',
    debriefing: 'idle',
  };

  for (const job of jobs) {
    const phase = job.phase as ProgressivePhase;
    if (phase in phaseStatusMap) {
      phaseStatusMap[phase] = job.status;
    }
  }

  const isProcessing = jobs.some((j) =>
    ['pending', 'processing', 'stitching'].includes(j.status)
  );

  const isComplete =
    phaseStatusMap.briefing === 'completed' &&
    (phaseStatusMap.simulator_stitched === 'completed' ||
      phaseStatusMap.simulator_main === 'completed');

  // ── Trigger functions ──────────────────────────────────────────────

  const triggerEarlyTranscription = useCallback(async () => {
    if (!sessionId || earlyTriggered) return;

    // Mark as triggered
    await supabase
      .from('sessions')
      .update({ early_transcription_triggered: true })
      .eq('id', sessionId);

    setEarlyTriggered(true);

    // Call the progressive transcription trigger
    const { data: authData } = await supabase.auth.getSession();
    const token = authData?.session?.access_token;

    // Use Inngest event send via API (direct Inngest invocation from frontend)
    // The early trigger cron will also pick this up as a backup
    try {
      await fetch('/api/inngest', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          name: 'transcription/progressive-submit',
          data: { sessionId, phase: 'briefing' },
        }),
      });
    } catch (err) {
      console.warn(
        '[useProgressiveTranscription] Failed to trigger via API, cron will pick up:',
        err
      );
    }
  }, [sessionId, earlyTriggered]);

  const triggerPhase = useCallback(
    async (phase: ProgressivePhase) => {
      if (!sessionId) return;

      try {
        const { data: authData } = await supabase.auth.getSession();
        const token = authData?.session?.access_token;

        await fetch('/api/inngest', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            name: 'transcription/progressive-submit',
            data: { sessionId, phase },
          }),
        });
      } catch (err) {
        console.warn(`[useProgressiveTranscription] Failed to trigger phase ${phase}:`, err);
      }
    },
    [sessionId]
  );

  // ── Cleanup ─────────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  return {
    phases: phaseStatusMap,
    jobs,
    isProcessing,
    isComplete,
    earlyTriggered,
    triggerEarlyTranscription,
    triggerPhase,
  };
}
